import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// import MagneticGrid from './originkit/ui/dot-image-reveal';
import LEDTicker from './originkit/ui/pixel-led-display';
import SpotlightReveal from './originkit/ui/image-spotlight';
import AsciiFire from './originkit/ui/ascii-flame';
// import InteractiveGrid from './originkit/ui/interactive-grid';
import ASCIIWaves from "./originkit/ui/character-waves";
import { ProjectCard } from './ProjectCard';
import projects from "../constants/data.json";
// import orangeSong from "../assets/sounds/orange-county.mp3";
// import CircularText from './originkit/ui/text-ring';


const Portfolio = ({ audioEnabled, onOpenMenu }) => {
    const [orangeSong, setOrangeSong] = useState(
        "https://res.cloudinary.com/dwbshru0j/video/upload/v1787588752/Portfolio%20Website/Others/orange-county_w5vksj.mp3"
    );

    const navigate = useNavigate();
    const [displayedText, setDisplayedText] = useState('');

    const fullText = 'Section UNDER DEVELOPEMENT...';

    // Keep track of uploaded object URL so it can be cleaned up
    const uploadedUrlRef = useRef(null);

    // Typewriter effect
    useEffect(() => {
        if (displayedText.length < fullText.length) {
            const timeout = setTimeout(() => {
                setDisplayedText(fullText.slice(0, displayedText.length + 1));
            }, 80);

            return () => clearTimeout(timeout);
        }
    }, [displayedText]);

    const gridRef = useRef(null);
    const targetRef = useRef({ x: 0, y: 0 });
    const currentRef = useRef({ x: 0, y: 0 });
    const rafRef = useRef(0);
    const audioRef = useRef(null);
    const audioElRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [entered, setEntered] = useState(false);
    const vinylRef = useRef(null);
    const tonearmRef = useRef(null);
    const animRef = useRef(0);

    useEffect(() => {
        const el = gridRef.current;
        if (!el) return;

        const maxX = 20; // px
        const maxY = 12; // px

        const onMove = (e) => {
            const rect = el.getBoundingClientRect();

            const clientX =
                e.clientX ??
                (e.touches && e.touches[0] && e.touches[0].clientX);

            const clientY =
                e.clientY ??
                (e.touches && e.touches[0] && e.touches[0].clientY);

            if (clientX == null || clientY == null) return;

            const nx = ((clientX - rect.left) / rect.width) * 2 - 1;
            const ny = ((clientY - rect.top) / rect.height) * 2 - 1;

            // move opposite to cursor
            targetRef.current.x = -nx * maxX;
            targetRef.current.y = -ny * maxY;
        };

        const loop = () => {
            const cur = currentRef.current;
            const tgt = targetRef.current;

            cur.x += (tgt.x - cur.x) * 0.05;
            cur.y += (tgt.y - cur.y) * 0.05;

            el.style.transform = `translate3d(${cur.x}px, ${cur.y}px, 0)`;

            rafRef.current = requestAnimationFrame(loop);
        };

        window.addEventListener('mousemove', onMove, { passive: true });
        window.addEventListener('touchmove', onMove, { passive: true });

        rafRef.current = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('touchmove', onMove);
        };
    }, []);

    /*
     * AUDIO HANDLING
     *
     * This effect runs whenever orangeSong changes.
     *
     * That means:
     * 1. Initial/default song loads.
     * 2. User selects another file.
     * 3. The audio element loads the new source.
     * 4. The new song automatically starts playing.
     */
    useEffect(() => {
        const a = audioElRef.current;
        if (!a) return;

        a.preload = 'auto';
        a.loop = true;

        try {
            a.volume = 0.7;
        } catch (e) {
            // Ignore volume errors
        }

        const playNewSong = async () => {
            try {
                // Stop current playback
                a.pause();

                // Start the new song from the beginning
                a.currentTime = 0;

                // Force the audio element to load the new source
                a.load();

                // Start playback
                await a.play();

                setIsPlaying(true);
            } catch (e) {
                /*
                 * Browser autoplay policies can prevent playback.
                 * In that case, the user can press the play button
                 * or click "CLICK TO ENTER".
                 */
                console.error('Could not play audio:', e);
                setIsPlaying(false);
            }
        };

        playNewSong();

        return () => {
            a.pause();
        };
    }, [orangeSong]);

    /*
     * Clean up uploaded object URLs when the component unmounts.
     */
    useEffect(() => {
        return () => {
            if (uploadedUrlRef.current) {
                URL.revokeObjectURL(uploadedUrlRef.current);
                uploadedUrlRef.current = null;
            }
        };
    }, []);

    // Prevent scrolling until user enters; restore on unmount or after enter
    useEffect(() => {
        if (!entered) {
            const prev = document.body.style.overflow;

            document.body.style.overflow = 'hidden';

            return () => {
                document.body.style.overflow = prev;
            };
        }

        // when entered, ensure overflow is restored
        document.body.style.overflow = '';

        return undefined;
    }, [entered]);

    /*
     * CLICK TO ENTER
     *
     * This is also a user interaction, so browsers are much more
     * likely to allow audio playback here.
     */
    const handleEnter = async () => {
        setEntered(true);

        const a = audioElRef.current;

        if (a) {
            try {
                await a.play();
                setIsPlaying(true);
            } catch (e) {
                console.error('Audio playback failed:', e);
                setIsPlaying(false);
            }
        }
    };

    /*
     * PLAY / PAUSE
     */
    const togglePlay = async () => {
        const a = audioElRef.current;

        if (!a) return;

        if (isPlaying) {
            a.pause();
            setIsPlaying(false);
        } else {
            try {
                await a.play();
                setIsPlaying(true);
            } catch (e) {
                console.error('Audio playback failed:', e);
                setIsPlaying(false);
            }
        }
    };

    /*
     * HANDLE AUDIO FILE UPLOAD
     *
     * When the user selects a file:
     * - Stop the old song.
     * - Revoke the previous object URL.
     * - Create a URL for the new file.
     * - Update orangeSong.
     *
     * Updating orangeSong triggers the audio useEffect above,
     * which automatically loads and plays the new song.
     */
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        // Make sure the selected file is actually audio
        if (!file.type.startsWith('audio/')) {
            console.error('Please select an audio file.');
            return;
        }

        // Stop currently playing audio immediately
        const a = audioElRef.current;

        if (a) {
            a.pause();
            a.currentTime = 0;
        }

        // Revoke the previous uploaded file URL
        if (uploadedUrlRef.current) {
            URL.revokeObjectURL(uploadedUrlRef.current);
        }

        // Create a temporary URL for the new file
        const url = URL.createObjectURL(file);

        // Keep reference so we can revoke it later
        uploadedUrlRef.current = url;

        // This triggers the audio effect above
        setOrangeSong(url);
    };

    /*
     * VINYL + TONEARM ANIMATION
     */
    useEffect(() => {
        let last = performance.now();

        const rot = { value: 0 };

        const loop = () => {
            const now = performance.now();
            const dt = (now - last) / 1000;

            last = now;

            if (isPlaying) {
                // spin vinyl
                rot.value = (rot.value + dt * 180) % 360;

                if (vinylRef.current) {
                    vinylRef.current.style.transform =
                        `rotate(${rot.value}deg)`;
                }

                // tonearm follows progress
                const a = audioElRef.current;

                let progress = 0;

                if (
                    a &&
                    a.duration &&
                    !isNaN(a.duration) &&
                    a.duration > 0
                ) {
                    progress = Math.min(1, a.currentTime / a.duration);
                }

                const angle = -10 + progress * 8;

                if (tonearmRef.current) {
                    tonearmRef.current.style.transform =
                        `rotate(${angle}deg)`;
                }
            } else {
                // paused: keep tonearm off record
                if (tonearmRef.current) {
                    tonearmRef.current.style.transform =
                        `rotate(-40deg)`;
                }
            }

            animRef.current = requestAnimationFrame(loop);
        };

        animRef.current = requestAnimationFrame(loop);

        return () => cancelAnimationFrame(animRef.current);
    }, [isPlaying]);

    return (
        <div className="relative w-full min-h-screen bg-black overflow-hidden items-center justify-center">

            {!entered && (
                <div
                    className="z-50 bg-black fixed inset-0 w-full h-screen text-7xl text-[#fffcd9cc] font-akshar flex items-center justify-center cursor-pointer text-center px-24"
                    onClick={handleEnter}
                    role="button"
                    aria-label="Enter site and start audio"
                >
                    CLICK TO ENTER
                </div>
            )}

            {/* Subtle background grid */}
            <div
                className="absolute inset-0"
                style={{ opacity: 0.3 }}
                ref={gridRef}
            >
                <svg
                    className="w-full h-full"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        <pattern
                            id="grid"
                            width="40"
                            height="40"
                            patternUnits="userSpaceOnUse"
                        >
                            <path
                                d="M 40 0 L 0 0 0 40"
                                fill="none"
                                stroke="#e48c1e"
                                strokeWidth="0.5"
                            />
                        </pattern>
                    </defs>

                    <rect
                        width="100%"
                        height="100%"
                        fill="url(#grid)"
                    />
                </svg>
            </div>

            {/* Main content - minimalistic */}
            <div className="absolute z-20 bg-black text-center w-full">

                <div className="h-24">
                    <LEDTicker
                        items={[
                            'WEB DEVELOPMENT',
                            'CRYPTOGRAPHY',
                            'SOFTWARE DEVELOPMENT',
                            'CLOUD',
                            'SYSTEMS'
                        ]}
                        speed={10}
                        color="#fffcd9cc"
                        textSize={40}
                        dotSize={5}
                        onColor="#fffcd9cc"
                    />
                </div>

                {/* 
                <label
                    htmlFor="file-input-button"
                    className="absolute bottom-5 cursor-pointer inline-flex items-center justify-center my-4"
                >
                    <input
                        id="file-input-button"
                        type="file"
                        className="hidden"
                    />
                    <CircularText
                        words={["UPLOAD", "FILE"]}
                        diameter={120}
                        font={{ fontSize: "12px" }}
                    />
                </label>
                */}

            </div>

            {/* AUDIO ELEMENT */}
            <audio
                ref={audioElRef}
                src={orangeSong}
                style={{ display: 'none' }}
                loop
            />

            <div className="relative grid md:mt-0 grid-cols-1 md:grid-cols-2 md:grid-rows-2 w-full h-screen">

                <div className="relative border rounded-xl bg-black w-full border-white border-opacity-10 z-10 pb-20 text-white border-l-0 flex items-end justify-center md:justify-start">

                    <div className="px-4 md:px-14 h-24 flex items-center gap-6 justify-center md:justify-start">

                        <div className="w-fit">
                            <h1 className="font-akshar text-7xl text-[#fffcd9cc]">
                                AGRAWAL
                            </h1>

                            <h1 className="font-akshar text-7xl text-[#fffcd9cc]">
                                DIVYA
                            </h1>
                        </div>

                        <div className="w-20">
                            <svg
                                aria-hidden="true"
                                viewBox="0 0 71 122"
                                className="framer-1togdmt"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{
                                    imageRendering: 'pixelated',
                                    flexShrink: 0
                                }}
                            >
                                <path
                                    d="M 71 122 L 71 18.61 C 67.659 86.985 22.158 116.026 0 122 Z"
                                    fill="#a08564"
                                />

                                <path
                                    d="M 0 0 L 71 0 L 71 70.305 L 0 70.305 Z"
                                    fill="#a08564"
                                />
                            </svg>
                        </div>

                    </div>
                </div>

                <div className="hidden relative md:flex flex-col text-right items-end pt-32 justify-end-safe pb-16 px-10 w-full border-white border-opacity-5 z-10 text-white">

                    <div
                        className="text-xl font-akshar text-[#fffcd9cc] cursor-pointer hover:bg-[#a08564]/80 w-fit px-2"
                        onClick={() => {
                            window.open(
                                'https://drive.google.com/file/d/1x-EwXwKYwb8rnu4rJXp1taWaxxBEDMb3/view',
                                '_blank'
                            );
                        }}
                    >
                        RESUME
                    </div>

                    <div
                        className="text-xl font-akshar text-[#fffcd9cc] cursor-pointer hover:bg-[#a08564]/80 w-fit px-2"
                        onClick={() => {
                            window.open(
                                'https://www.linkedin.com/in/divya--agrawal/',
                                '_blank'
                            );
                        }}
                    >
                        LINKEDIN
                    </div>

                    <div
                        className="text-xl font-akshar text-[#fffcd9cc] cursor-pointer hover:bg-[#a08564]/80 w-fit px-2"
                        onClick={() => {
                            window.open(
                                'https://github.com/divyaagrawal261',
                                '_blank'
                            );
                        }}
                    >
                        GITHUB
                    </div>

                    <div
                        className="text-xl font-akshar text-[#fffcd9cc] cursor-pointer hover:bg-[#a08564]/80 w-fit px-2"
                        onClick={() => {
                            window.open(
                                'https://leetcode.com/divyaagrawal261/',
                                '_blank'
                            );
                        }}
                    >
                        LEETCODE
                    </div>

                </div>

                <div className="relative w-full border-white border-opacity-5 z-10 text-white">

                    <div className="text-xl w-full md:w-4/5 font-akshar text-[#fffcd9cc] p-4 md:pt-14 md:pl-14">

                        I’m a final-year student with a keen interest in cryptography, software development, and building secure, scalable systems. I enjoy creating user-focused solutions that thoughtfully bridge technology, creativity, and real-world problems. I’m passionate about building, deploying, and managing software end-to-end, from writing secure applications to hosting services and managing infrastructure.

                        <br />

                        Currently pursuing an Integrated M.Tech in Information Technology at IIIT Gwalior.

                    </div>

                    <div className="md:hidden relative grid grid-cols-2 grid-rows-2 text-center items-center justify-end p-4 w-full border-white border-opacity-5 z-10 text-white gap-2">

                        <h2
                            className="text-xl font-akshar text-[#fffcd9cc] bg-[#a08564] rounded-lg px-2 cursor-pointer"
                            onClick={() => {
                                window.open(
                                    'https://drive.google.com/file/d/1x-EwXwKYwb8rnu4rJXp1taWaxxBEDMb3/view',
                                    '_blank'
                                );
                            }}
                        >
                            RESUME
                        </h2>

                        <h2
                            className="text-xl font-akshar text-[#fffcd9cc] bg-[#a08564] rounded-lg px-2 cursor-pointer"
                            onClick={() => {
                                window.open(
                                    'https://www.linkedin.com/in/divya--agrawal/',
                                    '_blank'
                                );
                            }}
                        >
                            LINKEDIN
                        </h2>

                        <h2
                            className="text-xl font-akshar text-[#fffcd9cc] bg-[#a08564] rounded-lg px-2 cursor-pointer"
                            onClick={() => {
                                window.open(
                                    'https://github.com/divyaagrawal261',
                                    '_blank'
                                );
                            }}
                        >
                            GITHUB
                        </h2>

                        <h2
                            className="text-xl font-akshar text-[#fffcd9cc] bg-[#a08564] rounded-lg px-2 cursor-pointer"
                            onClick={() => {
                                window.open(
                                    'https://leetcode.com/divyaagrawal261/',
                                    '_blank'
                                );
                            }}
                        >
                            LEETCODE
                        </h2>

                    </div>
                </div>

                <div className="relative rounded-xl grid grid-cols-2 grid-rows-2 px-2 py-2 gap-2 bg-black border border-r-0 border-b-0 w-full border-white border-opacity-10 z-10 text-white md:grid hidden">

                    <SpotlightReveal
                        image="https://res.cloudinary.com/dwbshru0j/image/upload/v1786273154/Portfolio%20Website/Lite/awtpa2cm7di0qlq1jego_lvcinm.jpg"
                    />

                    <SpotlightReveal
                        image="https://res.cloudinary.com/dwbshru0j/image/upload/v1786273154/Portfolio%20Website/Lite/kpea1thjl9kf64x6frn2_jwvedz.jpg"
                    />

                    <SpotlightReveal
                        image="https://res.cloudinary.com/dwbshru0j/image/upload/v1786273154/Portfolio%20Website/Lite/kaagdanrtlazo2sqnvp6_lvfehq.jpg"
                    />

                    <SpotlightReveal
                        image="https://res.cloudinary.com/dwbshru0j/image/upload/v1786273154/Portfolio%20Website/Lite/xbmhybsih21uiqpcbxju_jj7ohk.jpg"
                    />

                </div>
            </div>

            <div className="relative border border-white border-opacity-10 border-r-0 border-l-0 border-b-0 w-full bg-black flex flex-col items-center justify-center text-white text-2xl font-akshar pb-20 md:px-16 px-4">

                <h2 className="md:text-6xl text-5xl text-[#fffcd9cc] mt-12 w-full">
                    EXPERIENCE
                </h2>

                <div className="h-12 md:h-24"></div>

                <div className="flex flex-col w-full">

                    <div className="flex h-24 md:h-48 md:rotate-[-3deg]">

                        <div className="flex flex-col w-full">
                            <div className="w-full text-2xl md:text-4xl text-[#fffcd9cc]">
                                D.E. SHAW INDIA PVT. LTD.
                            </div>

                            <div className="w-full text-xl md:text-2xl text-[#fffcd9cc]">
                                SYSTEMS ENGINEERING INTERN
                            </div>

                            <div className="w-full text-lg md:text-xl text-[#fffcd9cc]">
                                MAY'26 - JULY'26
                            </div>
                        </div>

                        <div
                            className="w-full h-full mt-10 md:block hidden"
                            style={{ transform: 'rotate(9deg)' }}
                        >
                            <div className="absolute inset-0 bg-black/50"></div>

                            <img
                                src="https://res.cloudinary.com/dwbshru0j/image/upload/v1786273417/Portfolio%20Website/Experience/deshaw_ge6rvp.jpg"
                                className="w-full h-full object-cover rounded-xl"
                            />
                        </div>

                    </div>

                    <div className="md:h-24"></div>

                    <div className="md:mt-10 h-24 md:h-48 flex gap-10 md:rotate-[-3deg]">

                        <div className="-mt-10 w-full h-full md:block hidden">
                            <div className="absolute inset-0 bg-black/10"></div>

                            <img
                                src="https://res.cloudinary.com/dwbshru0j/image/upload/v1786273563/Portfolio%20Website/Experience/Flagright-logo_japvsw.webp"
                                className="w-full h-full object-cover rounded-xl"
                            />
                        </div>

                        <div className="flex flex-col w-full mt-12 md:rotate-[9deg]">

                            <div className="w-full text-2xl md:text-4xl text-[#fffcd9cc]">
                                FLAGRIGHT (YC W22)
                            </div>

                            <div className="w-full text-xl md:text-2xl text-[#fffcd9cc]">
                                SOFTWARE DEVELOPER INTERN
                            </div>

                            <div className="w-full text-lg md:text-xl text-[#fffcd9cc]">
                                AUGUST'25 - NOVEMBER'25
                            </div>

                        </div>

                    </div>

                    <div className="mt-20 md:mt-0 md:h-24"></div>

                    <div className="flex h-24 md:h-48 md:rotate-[-3deg]">

                        <div className="flex flex-col w-full">

                            <div className="w-full text-2xl md:text-4xl text-[#fffcd9cc]">
                                MEETMUX
                            </div>

                            <div className="w-full text-xl md:text-2xl text-[#fffcd9cc]">
                                SOFTWARE DEVELOPER INTERN
                            </div>

                            <div className="w-full text-lg md:text-xl text-[#fffcd9cc]">
                                AUGUST'24 - DECEMBER'24
                            </div>

                        </div>

                        <div
                            className="w-full h-full mt-10 md:block hidden"
                            style={{ transform: 'rotate(9deg)' }}
                        >
                            <div className="absolute inset-0 bg-black/50"></div>

                            <img
                                src="https://res.cloudinary.com/dwbshru0j/image/upload/v1770021390/Portfolio%20Website/Experience/MeetMux_ewblot.avif"
                                className="w-full h-full object-cover object-top rounded-xl"
                            />
                        </div>

                    </div>

                    <div className="md:h-24"></div>

                    <div className="md:mt-10 h-24 md:h-48 flex gap-10 md:rotate-[-3deg]">

                        <div className="-mt-10 w-full h-full md:block hidden">
                            <div className="absolute inset-0 bg-black/10"></div>

                            <img
                                src="https://res.cloudinary.com/dwbshru0j/image/upload/v1770021390/Portfolio%20Website/Experience/NCW_iglb9r.avif"
                                className="w-full h-full object-cover rounded-xl"
                            />
                        </div>

                        <div className="flex flex-col w-full mt-12 md:rotate-[9deg]">

                            <div className="w-full text-2xl md:text-4xl text-[#fffcd9cc]">
                                NATIONAL COMMISSION FOR WOMEN
                            </div>

                            <div className="w-full text-xl md:text-2xl text-[#fffcd9cc]">
                                WEB DEVELOPER INTERN
                            </div>

                            <div className="w-full text-lg md:text-xl text-[#fffcd9cc]">
                                MARCH'24 - APRIL'24
                            </div>

                        </div>

                    </div>

                    <div className="h-24"></div>

                    <div className="flex h-24 md:h-48 md:rotate-[-3deg]">

                        <div className="flex flex-col w-full">

                            <div className="w-full text-2xl md:text-4xl text-[#fffcd9cc]">
                                LUCO HOMES
                            </div>

                            <div className="w-full text-xl md:text-2xl text-[#fffcd9cc]">
                                WEB DEVELOPER INTERN
                            </div>

                            <div className="w-full text-lg md:text-xl text-[#fffcd9cc]">
                                FEBRUARY'24 - MARCH'24
                            </div>

                        </div>

                        <div
                            className="w-full h-full mt-10 md:block hidden"
                            style={{ transform: 'rotate(9deg)' }}
                        >
                            <div className="absolute inset-0 bg-black/50"></div>

                            <img
                                src="https://res.cloudinary.com/dwbshru0j/image/upload/v1770021388/Portfolio%20Website/Experience/Luco_Home_nv2mwx.avif"
                                className="w-full h-full object-cover rounded-xl"
                            />
                        </div>

                    </div>

                    <div className="md:h-24"></div>

                </div>
            </div>

            <div className="relative w-full bg-black flex flex-col justify-center text-white text-2xl font-akshar pb-20">

                <div className="absolute inset-0 w-full flex items-center justify-center blur-sm">
                    <ASCIIWaves />
                </div>

                <h2 className="text-6xl text-[#fffcd9cc] z-10 md:bg-black/90 w-fit p-4 md:p-12">
                    PROJECTS
                </h2>

                <div className="flex flex-col md:flex-row md:gap-10 px-4 md:px-14 w-full md:mt-10">

                    <div className="flex flex-col md:gap-10 w-full">

                        {projects
                            .filter((_, i) => i % 2 === 0)
                            .map((project, i) => (
                                <ProjectCard
                                    key={`${project.title}-${i}`}
                                    projectName={project.title}
                                    imageSrc={project.previewUrl}
                                    link={project.link}
                                    timeline={project.year}
                                    role={project.role}
                                />
                            ))}

                    </div>

                    <div className="flex flex-col md:gap-10 w-full md:-mt-28">

                        {projects
                            .filter((_, i) => i % 2 === 1)
                            .map((project, i) => (
                                <ProjectCard
                                    key={`${project.title}-${i}`}
                                    projectName={project.title}
                                    imageSrc={project.previewUrl}
                                    link={project.link}
                                    timeline={project.year}
                                    role={project.role}
                                />
                            ))}

                    </div>

                </div>

                {/* <InteractiveGrid /> */}

            </div>

            <div className="relative w-full">

                <div className="md:hidden inset-0 w-full flex">
                    <AsciiFire intensity={isPlaying ? 100 : 50} />
                </div>

                <div className="inset-0 w-full md:flex hidden">
                    <AsciiFire intensity={isPlaying ? 100 : 50} />
                    <AsciiFire intensity={isPlaying ? 100 : 50} />
                    <AsciiFire intensity={isPlaying ? 100 : 50} />
                </div>

                <div className="absolute z-20 text-[#fffcd9cc] inset-0 h-fit flex md:flex-row flex-col items-center justify-between text-2xl font-akshar">

                    <div className="w-fit p-14 flex-start">

                        <h1 className="font-akshar text-7xl text-[#fffcd9cc]">
                            AGRAWAL
                        </h1>

                        <h1 className="font-akshar text-7xl text-[#fffcd9cc]">
                            DIVYA
                        </h1>

                    </div>

                    <div className="flex items-center">

                        <div className="flex flex-col gap-4 md:p-14 px-4 flex-end">

                            <div className="bg-[#fffcd9cc] p-2 px-3 text-[#000700] rounded-xl hover:bg-[#000700]/80 hover:text-[#fffcd9cc] hover:border-[#fffcd9cc] border cursor-pointer">

                                <button
                                    className="text-center w-full"
                                    onClick={() => {
                                        navigate('/');
                                    }}
                                >
                                    MAIN PORTFOLIO
                                </button>

                            </div>

                            {/* AUDIO FILE UPLOAD */}

                            <div className="bg-[#fffcd9cc] p-2 px-3 text-[#000700] rounded-xl hover:bg-[#000700]/80 hover:text-[#fffcd9cc] hover:border-[#fffcd9cc] border cursor-pointer">

                                <button
                                    className="text-center w-full cursor-pointer"
                                    onClick={() => {
                                        window.open(
                                            'https://www.linkedin.com/in/divya--agrawal/',
                                            '_blank'
                                        );
                                    }}
                                >
                                    CONNECT WITH ME
                                </button>

                            </div>
                            <label className="bg-[#fffcd9cc] p-2 px-3 text-[#000700] rounded-xl hover:bg-[#000700]/80 hover:text-[#fffcd9cc] hover:border-[#fffcd9cc] border cursor-pointer block text-center">
                                CHANGE MUSIC
                                <input
                                    type="file"
                                    accept="audio/*"
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                            </label>

                        </div>

                        {/* VINYL PLAYER */}
                        <div className="md:p-14 px-4 md:pl-0">

                            <div
                                className="relative cursor-pointer select-none"
                                style={{
                                    width: '130px',
                                    height: '100px'
                                }}
                                aria-label={isPlaying ? 'Pause' : 'Play'}
                                onClick={togglePlay}
                            >

                                <div
                                    className="absolute inset-0 rounded-2xl"
                                    style={{
                                        background: '#766651',
                                        boxShadow:
                                            '0 0 0 1px rgba(255,255,255,0.12), inset 0 1px 0 rgba(255,255,255,0.06)'
                                    }}
                                />

                                <div
                                    className="absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-700"
                                    style={{
                                        opacity: 1,
                                        background:
                                            'radial-gradient(at 46px 50px, rgba(255,252,217,0.1) 0%, transparent 65%)'
                                    }}
                                />

                                <div
                                    className="absolute rounded-full"
                                    style={{
                                        width: '84px',
                                        height: '84px',
                                        top: '8px',
                                        left: '4px',
                                        background: '#0a0a0a',
                                        boxShadow:
                                            'inset 0 3px 16px rgba(0,0,0,0.9), inset 0 0 0 1px rgba(255,255,255,0.07)'
                                    }}
                                />

                                <div
                                    className="absolute rounded-full pointer-events-none transition-opacity duration-700"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        top: '10px',
                                        left: '6px',
                                        boxShadow:
                                            'rgba(160,133,100,0.18) 0px 0px 22px',
                                        opacity: 1
                                    }}
                                />

                                <div
                                    ref={vinylRef}
                                    className="absolute rounded-full overflow-hidden"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        top: '10px',
                                        left: '6px',
                                        willChange: 'transform',
                                        transform: 'rotate(9.98294deg)'
                                    }}
                                >

                                    <div
                                        className="absolute inset-0 rounded-full"
                                        style={{
                                            background: '#060606'
                                        }}
                                    />

                                    <svg
                                        className="absolute inset-0 w-full h-full"
                                        viewBox="0 0 104 104"
                                    >

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="14"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="16.6"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="19.2"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="21.8"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="24.4"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="27"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="29.6"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="32.2"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="34.8"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="37.4"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="40"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="42.6"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="45.2"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <circle
                                            cx="52"
                                            cy="52"
                                            r="47.8"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.07)"
                                            strokeWidth="0.7"
                                        />

                                        <path
                                            d="M 26 22 A 34 34 0 0 1 76 26"
                                            fill="none"
                                            stroke="rgba(255,255,255,0.10)"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                        />

                                    </svg>

                                    <div
                                        className="absolute rounded-full overflow-hidden"
                                        style={{
                                            width: '29px',
                                            height: '29px',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%,-50%)',
                                            boxShadow:
                                                '0 0 0 1px rgba(0,0,0,0.5)'
                                        }}
                                    >
                                        <img
                                            src="https://res.cloudinary.com/dwbshru0j/image/upload/v1786948963/Portfolio%20Website/Others/logo_rcdcqa.ico"
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    <div
                                        className="absolute rounded-full z-10"
                                        style={{
                                            width: '4px',
                                            height: '4px',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%,-50%)',
                                            background: '#111',
                                            boxShadow:
                                                '0 0 0 1.2px #444, 0 0 0 2px rgba(0,0,0,0.6)'
                                        }}
                                    />

                                </div>

                                <svg
                                    className="absolute inset-0 pointer-events-none"
                                    width="130"
                                    height="100"
                                    viewBox="0 0 130 100"
                                >

                                    <g
                                        ref={tonearmRef}
                                        style={{
                                            transformOrigin: '120px 9px',
                                            transform: 'rotate(-40deg)',
                                            transition:
                                                'transform 0.25s linear'
                                        }}
                                    >

                                        <polygon
                                            points="122,5 119,13 75,32 78,25"
                                            fill="#303030"
                                        />

                                        <line
                                            x1="120"
                                            y1="9"
                                            x2="77"
                                            y2="28"
                                            stroke="rgba(255,255,255,0.12)"
                                            strokeWidth="0.8"
                                            strokeLinecap="round"
                                        />

                                        <rect
                                            x="73"
                                            y="27"
                                            width="8"
                                            height="4"
                                            rx="1"
                                            fill="#252525"
                                            stroke="#3a3a3a"
                                            strokeWidth="0.5"
                                            transform="rotate(27,77,29)"
                                        />

                                    </g>

                                    <circle
                                        cx="120"
                                        cy="9"
                                        r="6"
                                        fill="#1c1c1c"
                                        stroke="#383838"
                                        strokeWidth="1.2"
                                    />

                                    <circle
                                        cx="120"
                                        cy="9"
                                        r="3"
                                        fill="#111"
                                        stroke="#444"
                                        strokeWidth="0.7"
                                    />

                                    <circle
                                        cx="120"
                                        cy="9"
                                        r="1.2"
                                        fill="#303030"
                                    />

                                </svg>

                                <div
                                    className="absolute z-30 flex items-center justify-center rounded-full"
                                    style={{
                                        width: '22px',
                                        height: '22px',
                                        top: '39px',
                                        left: '35px',
                                        background: 'rgba(0,0,0,0.55)',
                                        boxShadow:
                                            '0 0 0 1px rgba(255,255,255,0.10)'
                                    }}
                                >

                                    {isPlaying ? (
                                        <svg
                                            width="10"
                                            height="10"
                                            viewBox="0 0 24 24"
                                            fill="#fffcd9cc"
                                        >
                                            <rect
                                                x="5"
                                                y="4"
                                                width="5"
                                                height="16"
                                                rx="1.5"
                                            />

                                            <rect
                                                x="14"
                                                y="4"
                                                width="5"
                                                height="16"
                                                rx="1.5"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            width="10"
                                            height="10"
                                            viewBox="0 0 24 24"
                                            fill="#fffcd9cc"
                                        >
                                            <path d="M7 5v14l12-7L7 5z" />
                                        </svg>
                                    )}

                                </div>

                            </div>

                        </div>

                    </div>
                </div>
            </div>

            {/*
            <div className="relative z-10 text-center w-full mx-auto px-2">
            <div className="relative aspect-square h-96 mx-auto" style={{ transition: 'transform 0.1s linear' }}>
                <MagneticGrid dots={50} />
            </div>

            <div className="flex flex-col gap-4 pt-8">

                <button
                    onClick={() => onOpenMenu ? onOpenMenu() : navigate('/')}
                    className="relative px-6 py-2 text-custom-yellow font-sakana text-lg border border-custom-yellow bg-transparent hover:bg-custom-yellow/10 transition-all duration-300 group overflow-hidden"
                >
                    <span className="relative z-10">
                        Okay, Explore other pages
                    </span>

                    <div className="absolute inset-0 bg-custom-yellow/5 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </button>

                <a
                    href="mailto:divyaagrawal0747@gmail.com?subject=MISSION%20FAILURE:%20Page%20Under%20Construction"
                    className="relative px-6 py-2 text-custom-yellow/70 font-sakana text-lg border border-custom-yellow/50 bg-transparent hover:text-custom-yellow hover:border-custom-yellow hover:bg-custom-yellow/10 transition-all duration-300 group overflow-hidden"
                >
                    <span className="relative z-10">
                        File a disappointment
                    </span>

                    <div className="absolute inset-0 bg-custom-yellow/5 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </a>

            </div>
            </div>
            */}

        </div>
    );
};

export default Portfolio;